'use client';

/**
 * App-shell spinner matching `BrandedSpinner.tsx` visuals without Remotion.
 * Use `BrandedSpinner.tsx` inside Remotion compositions only (requires Remotion context).
 */
import React from 'react';
import { cn } from '@/lib/utils';

export interface BrandedSpinnerAppProps {
  logoUrl?: string;
  primaryColor?: string;
  /** Ring diameter in px */
  size?: number;
  opacity?: number;
  className?: string;
}

export function BrandedSpinnerApp({
  logoUrl,
  primaryColor = '#0d9488',
  size = 80,
  opacity = 1,
  className,
}: BrandedSpinnerAppProps) {
  const borderWidth = Math.max(3, Math.round(size * 0.04));
  const logoSize = Math.round(size * 0.55);

  return (
    <div
      className={cn('relative flex-shrink-0', className)}
      style={{ width: size, height: size, opacity }}
    >
      <div
        className="absolute inset-0 rounded-full animate-spin"
        style={{
          borderWidth,
          borderStyle: 'solid',
          borderColor: 'transparent',
          borderTopColor: primaryColor,
          borderRightColor: primaryColor,
          animationDuration: '1s',
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 rounded-full"
        style={{
          borderWidth,
          borderStyle: 'solid',
          borderColor: `${primaryColor}22`,
        }}
      />
      <div
        className="pointer-events-none absolute -inset-2 rounded-full"
        style={{
          background: `radial-gradient(circle, ${primaryColor}18 0%, transparent 70%)`,
        }}
      />
      {logoUrl ? (
        <div
          className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center overflow-hidden rounded-full"
          style={{ width: logoSize, height: logoSize }}
        >
          {/* Avatar URLs are often signed/external — Next/Image hostname config varies */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={logoUrl} alt="" className="size-full rounded-full object-contain" />
        </div>
      ) : null}
    </div>
  );
}
